'use client';

/**
 * useHallOfShame Hook
 *
 * Fetches deprecated, abandoned, and rugged protocols from the cache.
 * Used for the "Hall of Shame" UI feature.
 */
import { useState, useEffect, useCallback } from 'react';
import type { HallOfShameProtocol } from '@/lib/masterchef-cache/types';
interface UseHallOfShameReturn {
  protocols: HallOfShameProtocol[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}
export function useHallOfShame(chainId: number): UseHallOfShameReturn {
  const [protocols, setProtocols] = useState<HallOfShameProtocol[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const fetchProtocols = useCallback(async () => {
    if (!chainId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/masterchef/protocols/deprecated?chainId=${chainId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch protocols: ${response.statusText}`);
      }
      const data = await response.json();
      setProtocols(data.protocols || []);
    } catch (err) {
      console.error('[useHallOfShame] Error:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      setProtocols([]);
    } finally {
      setIsLoading(false);
    }
  }, [chainId]);
  useEffect(() => {
    fetchProtocols();
  }, [fetchProtocols]);
  return {
    protocols,
    isLoading,
    error,
    refetch: fetchProtocols
  };
}